import { Button } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { notify } from "@/ui/chakra/notify";
import { useConfirmToggle } from "@/ui/modals/useConfirm";
import { Table } from "@/ui/tables/Table";
import { getStoredPageSize } from "@/ui/tables/pageSize";
import { LuPencil, LuPlus } from "react-icons/lu";
import {
  ProductAdjustment,
  useGetProductAdjustments,
  useGetProductAdjustmentTypes,
  type ProductAdjustmentFilters,
} from "@/shared/core-product-adjustments";
import { TableFilters } from "@/ui/filters/TableFilters";
import {
  countActiveFilters,
  getDateRange,
  getOption,
  toDateRangeParams,
  type FilterField,
  type FilterValues,
} from "@/ui/filters/filterValues";
import { useStorageFilterField } from "@/core/storages/useCases/useStorageFilterField";
import type { Pagination } from "@/shared/core-configs";
import { PageContainer } from "@/ui/containers/PageContainer";
import { useDebounce } from "@/core/common/hooks/useDebounce";
import { useAuth } from "@/shared/session";
import { ProductAdjustmentTableColumns } from "../Columns";
import { CreateProductAdjustmentModal } from "../components/CreateProductAdjustmentModal";
import { EditProductAdjustmentModal } from "../components/EditProductAdjustmentModal";
import { useToggleEnabledProductAdjustment } from "../useCases/useToggleEnabledProductAdjustment";
import AdjustmentProductsModal from "../components/AdjustmentProductsModal";

export const ProductAdjustmentPage = () => {
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: getStoredPageSize(),
  });

  const [showDisabled, setShowDisabled] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>();
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [order, setOrder] = useState<{
    sort?: string;
    order?: string;
  }>({
    sort: undefined,
    order: undefined,
  });

  const { session } = useAuth();

  const { data: adjustmentTypes } = useGetProductAdjustmentTypes();
  const storageField = useStorageFilterField();
  const filterFields = useMemo<FilterField[]>(
    () => [
      { type: "dateRange", key: "date", label: "Fecha de creación" },
      {
        type: "select",
        key: "type",
        label: "Tipo de reajuste",
        tagLabel: "Tipo",
        options: adjustmentTypes.map((t) => ({ value: t.id, label: t.name })),
      },
      storageField,
    ],
    [adjustmentTypes, storageField],
  );
  const [extraFilters, setExtraFilters] = useState<FilterValues>({});
  const filters = useMemo<ProductAdjustmentFilters>(
    () => ({
      ...toDateRangeParams(getDateRange(extraFilters, "date")),
      productAdjustmentTypeId: getOption(extraFilters, "type")?.value,
      storageId: getOption(extraFilters, "storage")?.value,
    }),
    [extraFilters],
  );

  const {
    data: productAdjustments,
    isPending,
    pagination: metaProductAdjustment,
  } = useGetProductAdjustments({
    page: pagination.page,
    pageSize: pagination.pageSize,
    searchTerm: debouncedSearchTerm,
    zoneId: session?.zone ?? undefined,
    showDisabled: showDisabled,
    ...order,
    filters,
  });

  const { mutate: toggleEnabledProductAdjustment } =
    useToggleEnabledProductAdjustment();
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isOpenAdjustmentProductsModal, setIsOpenAdjustmentProductsModal] =
    useState(false);
  const [selectedProductAdjustment, setSelectedProductAdjustment] =
    useState<ProductAdjustment | null>(null);

  const confirmToggle = useConfirmToggle();

  const handleToggleEnabled = useCallback(
    async (item: ProductAdjustment) => {
      const ok = await confirmToggle(
        Boolean(item.enabled),
        `el reajuste "${item.description}"`,
      );
      if (!ok) return;
      toggleEnabledProductAdjustment(
        {
          id: item.id,
          enabled: !item.enabled,
        },
        {
          onSuccess: () =>
            notify.success(
              item.enabled ? "Reajuste deshabilitado" : "Reajuste habilitado",
            ),
          onError: (error) =>
            notify.error("No se pudo actualizar el reajuste", error),
        },
      );
    },
    [toggleEnabledProductAdjustment, confirmToggle],
  );

  const handleOnClickProducts = useCallback((item: ProductAdjustment) => {
    setIsOpenAdjustmentProductsModal(true);
    setSelectedProductAdjustment(item);
  }, []);

  const productAdjustmentColumns = useMemo(
    () =>
      ProductAdjustmentTableColumns(handleToggleEnabled, handleOnClickProducts),
    [handleToggleEnabled, handleOnClickProducts],
  );

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [debouncedSearchTerm, filters, showDisabled]);

  const handleOnSortChange = (sort: string, order: string) => {
    setOrder({ sort, order });
  };

  const handleOpenCreateModal = () => {
    setSelectedProductAdjustment(null);
    setIsCreateModalOpen(true);
  };
  return (
    <PageContainer
      display="flex"
      flexDir="column"
      gap={4}
      title="Reajuste de productos"
      description="Controla los reajustes de productos y su estado actual."
      actions={
        <Button colorPalette="brand" onClick={handleOpenCreateModal}>
          <LuPlus />
          Crear reajuste
        </Button>
      }
    >
      <Table
        searchPlaceholder="Buscar por tipo o descripción…"
        pageSize={pagination.pageSize}
        onChangePageSize={(pageSize) => setPagination({ page: 1, pageSize })}
        isLoading={isPending}
        page={pagination.page}
        hasActiveFilters={countActiveFilters(extraFilters) > 0}
        onClearFilters={() => setExtraFilters({})}
        filters={
          <TableFilters
            fields={filterFields}
            value={extraFilters}
            onChange={setExtraFilters}
          />
        }
        emptyDescription="Un reajuste corrige el stock de uno o más productos."
        emptyAction={
          <Button
            colorPalette="brand"
            size="sm"
            onClick={handleOpenCreateModal}
          >
            Crear reajuste
          </Button>
        }
        data={productAdjustments}
        headers={productAdjustmentColumns}
        extractItemKey={(item) => item.id}
        pages={metaProductAdjustment?.totalPages}
        totalItems={metaProductAdjustment?.count}
        itemsLabel="reajustes"
        showPagination
        showDisabledCheck
        onShowDisabledChange={setShowDisabled}
        onSortChange={handleOnSortChange}
        onSearch={setSearchTerm}
        onChangePage={(page) =>
          setPagination({ ...pagination, page: page + 1 })
        }
        hiddenColumnsKey="productAdjustments"
        actions={[
          {
            label: "Editar",
            onClick: (item) => {
              setIsOpenEditModal(true);
              setSelectedProductAdjustment(item as ProductAdjustment);
            },
            icon: <LuPencil size={18} />,
          },
        ]}
      />
      {isCreateModalOpen && (
        <CreateProductAdjustmentModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
        />
      )}
      {isOpenEditModal && selectedProductAdjustment && (
        <EditProductAdjustmentModal
          isOpen={isOpenEditModal}
          onClose={() => setIsOpenEditModal(false)}
          defaultValues={selectedProductAdjustment}
        />
      )}
      {isOpenAdjustmentProductsModal && selectedProductAdjustment && (
        <AdjustmentProductsModal
          isOpen={isOpenAdjustmentProductsModal}
          onClose={() => setIsOpenAdjustmentProductsModal(false)}
          selectedProductAdjustment={selectedProductAdjustment}
        />
      )}
    </PageContainer>
  );
};
